// O2-O5 (QO2_Change, QO3_Stuck, QO4_Tone, QO5_CheckIns): one-tap questions on the XOnb template.
// Answers merge into the onboarding draft on Next (SPEC §2). O2 also takes "Or say it in your own words" (voice).
import { useState } from 'preact/hooks';
import type { CheckIn } from '@shared/types';
import { OB_CHANGE, OB_CHECKIN, OB_STUCK, OB_TONE } from '@shared/content/lines';
import { Screen, XOnb, Icon, Waveform, useHoldToTalk, type IconName, type XOnbOption } from '../../components';
import { api } from '../../api/client';
import { errorText, toast } from '../../store';
import { back, go } from '../../router';
import { RecorderError } from '../../audio/recorder';
import { matchChange } from '../../content/onboarding';
import { draftStore, progress, setDraft, setOb, useDraft } from './draft';
import qs from './Questions.module.css';

const CHANGE_ICONS: IconName[] = ['briefcase', 'mail', 'home', 'pulse', 'bookOpen', 'moon'];
const STUCK_ICONS: IconName[] = ['start', 'wave', 'finish', 'bell'];

export function micError(e: RecorderError, fallback: string) {
  toast(e.kind === 'denied' ? `Microphone access is off. ${fallback}` : e.kind === 'no-mic' ? `No microphone found. ${fallback}` : `Couldn't use the microphone. ${fallback}`);
}

export function O2Change() {
  const saved = useDraft((d) => d.ob.change);
  const savedSaid = useDraft((d) => d.ob.changeSaid);
  const invite = useDraft((d) => d.invite);
  const [chosen, setChosen] = useState<string | null>(saved);
  const [heard, setHeard] = useState<string | null>(savedSaid);
  const [busy, setBusy] = useState(false);
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  // What the user said or typed. It picks an option only when it clearly matches one; otherwise their own words are
  // the answer (sent as changeSaid). Picking an option afterwards wins; tapping the words again re-selects them.
  const takeWords = (text: string) => {
    const t = text.trim().replace(/\s+/g, ' ').slice(0, 200);
    if (!t) return;
    setHeard(t);
    setChosen(matchChange(t));
    setTyping(false); setTyped('');
  };
  const talk = useHoldToTalk({
    tap: 'toggle',
    onAudio: async (clip) => {
      setBusy(true);
      try {
        const r = await api.sttPublic({ invite, audio: { b64: clip.b64, mime: clip.mime }, hint: 'change' });
        const text = (r.text || '').trim();
        if (!text) { toast("Didn't catch that. Try again, or pick one."); return; }
        takeWords(text);
      } catch (e) { toast(errorText(e)); } finally { setBusy(false); }
    },
    onError: (e) => { micError(e, 'You can type it instead.'); setTyping(true); },
    onTooShort: () => toast('Hold a little longer, or tap once to start and once to stop.'),
  });
  const live = talk.phase === 'recording' || talk.phase === 'cancel' || talk.phase === 'starting';
  const opts: XOnbOption[] = OB_CHANGE.map((o, i) => ({ key: o.label, label: o.label, sub: o.sub, icon: CHANGE_ICONS[i] }));
  const own = !!heard && !chosen;
  const answered = !!chosen || own;
  // Keep the words with an option only when they led to it (a different pick means they were about something else).
  const said = heard && (own || matchChange(heard) === chosen) ? heard : null;
  return (
    <Screen theme="pre" bg="#100E0C" id="O2">
      <XOnb
        title="What do you most want to change?" sub="Pick one. You can add more later." layout="grid" options={opts}
        selected={chosen ? [chosen] : []} onPick={(k) => setChosen(k)} optionHeight={112}
        pct={progress('O2', answered)} onBack={() => back('O1')} canNext={answered}
        onNext={() => { setOb({ change: chosen, changeSaid: said }); go('O3'); }}
        voice={
          <div style={{ position: 'relative', flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '18px 20px 0' }}>
            {heard && (
              <button
                type="button" class={qs.own} data-on={own ? 'true' : 'false'} aria-pressed={own ? 'true' : 'false'}
                onClick={() => setChosen(null)} aria-label={`In your own words: ${heard}`}
              >
                <span class={qs.ownHead}>IN YOUR OWN WORDS</span>
                <span class={qs.ownText}>{`"${heard}"`}</span>
                {own && <span class={qs.ownCheck} aria-hidden="true"><Icon name="check" size={14} strokeWidth={3} color="var(--on)" /></span>}
              </button>
            )}
            {typing && (
              <form class={qs.typeRow} onSubmit={(e) => { e.preventDefault(); takeWords(typed); }}>
                <input
                  class={qs.typeInput} type="text" value={typed} placeholder="Type it in your own words" aria-label="Type it in your own words"
                  maxLength={200} autoComplete="off" enterKeyHint="done" autoFocus onInput={(e) => setTyped((e.currentTarget as HTMLInputElement).value)}
                />
                <button type="submit" class={qs.typeUse} disabled={!typed.trim()}>Use this</button>
              </form>
            )}
            <button
              type="button" {...talk.bind} aria-pressed={live ? 'true' : 'false'}
              aria-label={live ? 'Stop and use what I said' : 'Or say it in your own words. Hold to talk, or tap to start and stop.'}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, minHeight: 44, padding: '0 16px', borderRadius: 22, touchAction: 'none',
                border: `1px solid ${live ? 'var(--acc)' : 'rgba(255,255,255,0.2)'}`, background: live ? 'var(--acc-a18)' : 'rgba(245,241,234,0.06)',
                color: '#F5F1EA', fontSize: 14, fontWeight: 700, WebkitTouchCallout: 'none',
              }}
            >
              {live ? <Waveform levels={talk.levels} bars={8} height={18} color="var(--acc)" /> : <Icon name="mic" size={18} color="var(--acc)" />}
              <span>{busy || talk.phase === 'sending' ? 'One moment…' : live ? 'Listening… tap to send' : 'Or say it in your own words'}</span>
            </button>
            {!typing && !live && <button type="button" class={qs.typeLink} onClick={() => setTyping(true)}>Or type it</button>}
          </div>
        }
      />
    </Screen>
  );
}

export function O3Stuck() {
  const saved = useDraft((d) => d.ob.stuck);
  const [chosen, setChosen] = useState<string | null>(saved);
  const opts: XOnbOption[] = OB_STUCK.map((o, i) => ({ key: o.label, label: o.label, sub: o.sub, icon: STUCK_ICONS[i] }));
  return (
    <Screen theme="pre" bg="#100E0C" id="O3">
      <XOnb
        title="When do you usually get stuck?" sub="Quick answer. No wrong ones." layout="list" options={opts}
        selected={chosen ? [chosen] : []} onPick={setChosen} optionHeight={76}
        pct={progress('O3', !!chosen)} onBack={() => back('O2')} canNext={!!chosen}
        onNext={() => {
          // O8's opening question follows this answer: a new answer (before the first card is done) starts O8 over.
          if (chosen !== saved && !draftStore.get().first) setDraft({ said: null, fh: null, fixes: [], smaller: false, cant: false, reason: null, fh2: null, laterIdx: 0, workCard: null, evenSmaller: null });
          setOb({ stuck: chosen }); go('O4');
        }}
      />
    </Screen>
  );
}

export function O4Tone() {
  const saved = useDraft((d) => d.ob.tone);
  const [chosen, setChosen] = useState<string[]>(saved);
  const opts: XOnbOption[] = OB_TONE.map((o) => ({ key: o.label, label: o.label, sub: o.example }));
  // Up to two: a third pick drops the oldest (prototype behaviour).
  const pick = (k: string) => setChosen((c) => (c.includes(k) ? c.filter((x) => x !== k) : c.concat([k]).slice(-2)));
  return (
    <Screen theme="pre" bg="#100E0C" id="O4">
      <XOnb
        title="How do you like to be talked to?" sub="Pick up to two." layout="grid" options={opts} exampleSubs
        selected={chosen} onPick={pick} optionHeight={132}
        pct={progress('O4', chosen.length > 0)} onBack={() => back('O3')} canNext={chosen.length > 0}
        onNext={() => { setOb({ tone: chosen }); go('O5'); }}
      />
    </Screen>
  );
}

export function O5CheckIns() {
  const saved = useDraft((d) => d.ob.checkin);
  const [chosen, setChosen] = useState<CheckIn>(saved || 'steady');
  const opts: XOnbOption[] = OB_CHECKIN.map((o) => ({ key: o.id, label: o.label, sub: o.sub, tag: 'tag' in o ? o.tag : undefined }));
  return (
    <Screen theme="pre" bg="#100E0C" id="O5">
      <XOnb
        title="How closely should they keep up with you?" layout="list" options={opts}
        selected={[chosen]} onPick={(k) => setChosen(k as CheckIn)} optionHeight={92}
        pct={progress('O5', true)} onBack={() => back('O4')} canNext foot="You can change this anytime in Me."
        onNext={() => { setOb({ checkin: chosen }); go('O6'); }}
      />
    </Screen>
  );
}
